import type { Company } from '@shared/types/index.ts';
import type { PlanFeature, Permission } from '@shared/constants/index.ts';
import { companyHasPlanFeature } from './plan-gate.tsx';
import type { IconName } from './icons.tsx';

/**
 * What each audience's navigation offers, in the one shape the app shell
 * (components/app-shell.tsx) draws. The shell never decides who may see what;
 * the lists and filters here do, and the server refuses whatever slips past.
 *
 * Home is for everyone who reads quotes: owners and managers get the owner
 * dashboard, and estimators and read-only viewers the sales dashboard
 * (pages/dashboard.tsx picks by `reports:read`). Logs are behind logs:read
 * (owner only); the items hide for everyone the endpoints would refuse anyway.
 *
 * The sidebar groups the destinations by the kind of work they are, under
 * small muted labels. Home stands alone at the top and Settings is pinned at
 * the bottom; between them the groups follow the work an office does —
 * win the customer and quote the job (Sales), schedule and carry it out
 * (Work), then collect payment and review the results (Business). Within a
 * group the order is the one this navigation has always had, so the daily
 * workflow still reads Home, Customers, Messages, Quotes, Schedule, Invoices,
 * Reports from top to bottom. Feedback is a product tool rather than a part
 * of the work, so it sits with Settings at the foot of the sidebar.
 *
 * `feature` hides an item the PLAN does not carry, on the same principle:
 * a company on Free has customers, quotes and settings, and no navigation to
 * four screens that would each answer 402. The pages themselves still explain
 * themselves to anyone who arrives by URL or bookmark (components/plan-gate).
 *
 * Extra work used to be hidden from residential companies (`commercialOnly`):
 * it could only ride a commercial customer's monthly invoice. Since it can be
 * added to any visit (migration 0104) every company that invoices has a use
 * for it, so it is gated like any other item.
 *
 * The Work hub (`/work`, pages/work-hub.tsx) gathers the secondary office
 * tools — Job chat, Staff, Visit coverage, Inspections, Extra work and Logs —
 * onto one page, the way the phone's More sheet gathers whatever has no tab.
 * It is behind the `FEATURE_NAV_SECONDARY_HUB` switch (`company.navSecondaryHub`):
 * off, the sidebar is the one it has always been and there is no hub. On,
 * those tools leave the desktop sidebar, which keeps Home; Customers,
 * Messages and Quotes; Schedule and Work; Invoices and Reports; then the
 * footer. The phone's More sheet and the command palette still list each
 * tool, and every address still works.
 */
export type NavGroup = 'home' | 'sales' | 'work' | 'business' | 'footer';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: IconName;
  group: NavGroup;
  permission?: Permission;
  feature?: PlanFeature;
  /** A deployment-wide switch, as opposed to a per-company plan feature. */
  deploymentFlag?: 'feedback' | 'helpAssistant' | 'navSecondaryHub';
  /**
   * `hubOnly`: a secondary office tool. The Work hub lists it, with the same
   * gates as here, and the hub's nav entry shows only while one of them can
   * be reached. While the hub is shown, the desktop sidebar leaves the tool
   * out (app-layout.tsx); the phone's More sheet and the palette keep it.
   */
  sidebar?: 'hubOnly';
  /** The line under a hub tool's name on the Work hub. */
  hubDescriptionKey?: string;
  /**
   * A module the deployment advertises only once its capability endpoint
   * answers (`/staff/visit-offers/capability`); see useVisitCoverage.
   */
  capability?: 'visitCoverage';
}

/** The Work hub's address. `/more` redirects here (app.tsx). */
export const WORK_HUB_PATH = '/work';

export const OFFICE_NAV_ITEMS: ReadonlyArray<NavItem> = [
  {
    to: '/dashboard',
    labelKey: 'nav.dashboard',
    icon: 'home',
    group: 'home',
    permission: 'quotes:read',
    // Every plan: Free's dashboard is the short one (pages/dashboard.tsx).
    feature: 'quote_payments',
  },
  { to: '/customers', labelKey: 'nav.customers', icon: 'users', group: 'sales' },
  // Beside customers, because that is what a conversation is about — and
  // ahead of quotes, because a customer waiting on an answer is more urgent
  // than a quote waiting to be written. `messaging` is a paid feature, so a
  // Free account does not get a link that can only load an error.
  {
    to: '/messages',
    labelKey: 'nav.messages',
    icon: 'message',
    group: 'sales',
    permission: 'customers:read',
    feature: 'messaging',
  },
  // One entry for quotes, however they are made. Magic Quote used to stand
  // beside it as an item of its own, and two doors to the same work read as
  // two kinds of work: the walkthroughs now live on a tab under Quotes
  // (components/quote/quotes-tabs), and starting one is a choice on the "New
  // quote" page beside Quick Quote and the editor (components/quote/quote-
  // methods).
  { to: '/quotes', labelKey: 'nav.quotes', icon: 'quote', group: 'sales' },
  { to: '/schedule', labelKey: 'nav.schedule', icon: 'calendar', group: 'work', feature: 'scheduling' },
  // The hub for the secondary tools below: one entry beside Schedule rather
  // than five more peers.
  { to: WORK_HUB_PATH, labelKey: 'nav.workHub', icon: 'tools', group: 'work', deploymentFlag: 'navSecondaryHub' },
  {
    to: '/chats',
    labelKey: 'jobChat.open',
    icon: 'chats',
    group: 'work',
    sidebar: 'hubOnly',
    hubDescriptionKey: 'workHub.tiles.chats',
  },
  {
    to: '/staff',
    labelKey: 'staff.title',
    icon: 'team',
    group: 'work',
    feature: 'staff_portal',
    permission: 'staff_portal:read',
    sidebar: 'hubOnly',
    hubDescriptionKey: 'workHub.tiles.staff',
  },
  // Its pages live under /staff, but it is a destination of its own: the
  // shell marks only the most specific entry current, so Staff is not lit
  // while coverage is open. After Staff, as the crew's navigation has it.
  {
    to: '/staff/visit-offers',
    labelKey: 'nav.visitCoverage',
    icon: 'coverage',
    group: 'work',
    feature: 'staff_portal',
    permission: 'staff_portal:read',
    capability: 'visitCoverage',
    sidebar: 'hubOnly',
    hubDescriptionKey: 'workHub.tiles.visitCoverage',
  },
  {
    to: '/inspections',
    labelKey: 'nav.inspections',
    icon: 'inspection',
    group: 'work',
    permission: 'schedule:read',
    sidebar: 'hubOnly',
    hubDescriptionKey: 'workHub.tiles.inspections',
  },
  {
    to: '/extra-work',
    labelKey: 'nav.changeOrders',
    icon: 'change',
    group: 'work',
    permission: 'quotes:read',
    feature: 'invoicing',
    sidebar: 'hubOnly',
    hubDescriptionKey: 'workHub.tiles.changeOrders',
  },
  {
    to: '/invoices',
    labelKey: 'nav.invoices',
    icon: 'invoice',
    group: 'business',
    permission: 'payments:read',
    feature: 'invoicing',
  },
  { to: '/reports', labelKey: 'nav.reports', icon: 'chart', group: 'business', permission: 'reports:read' },
  {
    to: '/logs',
    labelKey: 'nav.logs',
    icon: 'history',
    group: 'business',
    permission: 'logs:read',
    sidebar: 'hubOnly',
    hubDescriptionKey: 'workHub.tiles.logs',
  },
  // `deploymentFlag` rather than `feature`: the plan features above are a
  // property of the COMPANY, while this is a switch the deployment's operator
  // throws for everybody (`feedback_enabled`). Its off state closes the API
  // too — the entry disappearing is the courtesy, not the control.
  {
    to: '/feedback',
    labelKey: 'nav.feedback',
    icon: 'feedback',
    group: 'footer',
    permission: 'feedback:view',
    deploymentFlag: 'feedback',
  },
  { to: '/help', labelKey: 'help.title', icon: 'help', group: 'footer', deploymentFlag: 'helpAssistant' },
  { to: '/settings', labelKey: 'nav.settings', icon: 'settings', group: 'footer' },
];

/** The labelled groups between Home and the pinned footer, in sidebar order. */
export const OFFICE_GROUPS: ReadonlyArray<{ id: NavGroup; labelKey: string }> = [
  { id: 'sales', labelKey: 'nav.groupSales' },
  { id: 'work', labelKey: 'nav.groupWork' },
  { id: 'business', labelKey: 'nav.groupBusiness' },
];

/**
 * Which destinations earn a place in the phone's bottom bar, best first. The
 * first four this person can reach get a tab; everything else, Settings
 * included, is one tap away under "More". Chosen from what an office opens
 * all day on a phone — the plan and permission gates still decide, so a
 * Free account that has no Schedule gets the next one down instead of a gap.
 */
export const OFFICE_PHONE_TAB_ORDER: ReadonlyArray<string> = [
  '/dashboard',
  '/customers',
  '/quotes',
  '/schedule',
  '/messages',
  '/invoices',
  '/reports',
];

/** What decides whether an entry is offered, read from the session and the deployment. */
export interface NavGate {
  can: (permission: Permission) => boolean;
  company: Company | null;
  feedbackEnabled: boolean;
  helpAssistant: boolean;
  visitCoverage: boolean;
}

/**
 * The entries of `items` this person can actually reach, in order. The Work
 * hub's own entry goes too when none of the tools it lists can be reached:
 * a hub that could only show its empty state is not worth a place in the bar.
 */
export function reachableNavItems(items: ReadonlyArray<NavItem>, gate: NavGate): NavItem[] {
  const reachable = items.filter(
    (item) =>
      (item.permission === undefined || gate.can(item.permission)) &&
      (item.feature === undefined || companyHasPlanFeature(gate.company, item.feature)) &&
      // The staff portal is offered only on the company's explicit word: a
      // missing plan counts as entitled everywhere else, but not here.
      (item.feature !== 'staff_portal' || gate.company?.planFeatures?.includes('staff_portal') === true) &&
      (item.deploymentFlag !== 'feedback' || gate.feedbackEnabled) &&
      (item.deploymentFlag !== 'helpAssistant' || gate.helpAssistant) &&
      (item.deploymentFlag !== 'navSecondaryHub' || gate.company?.navSecondaryHub === true) &&
      (item.capability !== 'visitCoverage' || gate.visitCoverage),
  );
  const hubHasTools = reachable.some((item) => item.sidebar === 'hubOnly');
  return reachable.filter((item) => item.to !== WORK_HUB_PATH || hubHasTools);
}

/** The secondary tools the Work hub lists for this person, in sidebar order. */
export function workHubItems(gate: NavGate): NavItem[] {
  return reachableNavItems(OFFICE_NAV_ITEMS, gate).filter((item) => item.sidebar === 'hubOnly');
}

/**
 * The crew's navigation: the same frame as the office's, holding only what a
 * cleaner works with. Their jobs are Home; Work holds the job chat and the
 * staff hub; their account sits where Settings does. The crew allowlist in
 * app.tsx (RequireAuth) still refuses every other page, whatever is offered
 * here. Notifications are the top bar's bell, as in the office.
 */
export const CREW_NAV_ITEMS: ReadonlyArray<NavItem> = [
  { to: '/my-jobs', labelKey: 'crew.title', icon: 'home', group: 'home' },
  { to: '/chats', labelKey: 'jobChat.open', icon: 'chats', group: 'work' },
  { to: '/staff', labelKey: 'staff.title', icon: 'team', group: 'work', feature: 'staff_portal' },
  {
    to: '/staff/visit-offers',
    labelKey: 'nav.visitCoverage',
    icon: 'coverage',
    group: 'work',
    feature: 'staff_portal',
    permission: 'staff_portal:read',
    capability: 'visitCoverage',
  },
  { to: '/crew-account', labelKey: 'crew.account', icon: 'settings', group: 'footer' },
];

export const CREW_GROUPS: ReadonlyArray<{ id: NavGroup; labelKey: string }> = [
  { id: 'work', labelKey: 'nav.groupWork' },
];

/** A cleaner's phone: their jobs first, then the conversations about them. */
export const CREW_PHONE_TAB_ORDER: ReadonlyArray<string> = ['/my-jobs', '/chats', '/staff', '/staff/visit-offers'];
